import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { BrightsideUiModule, HeaderLabelCellConfig } from '@brightside/brightside-ui';
import { CurrencyPipe, NgIf } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { NewSpendingAccountService, SpendingAccount } from '@brightside-web/desktop/feature/spending-account';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SpendingCardService } from '../../spending-card.service';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';

@Component({
    selector: 'brightside-web-spending-complete-card',
    templateUrl: './spending-complete-card.component.html',
    styleUrls: ['./spending-complete-card.component.scss'],
    imports: [BrightsideUiModule, NgIf, TranslateModule],
    providers: [CurrencyPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms ease-out', style({ opacity: 1 }))
            ])
        ])
    ]
})
export class SpendingCompleteCardComponent implements OnDestroy {
  private _spendingSvc: NewSpendingAccountService = inject(NewSpendingAccountService);

  private _router: Router = inject(Router);
  private _currencyPipe: CurrencyPipe = inject(CurrencyPipe);
  private _cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _translateSvc: TranslateService = inject(TranslateService);

  private _serviceInventory = inject(ServiceInventoryService);
  private _sub = new Subscription();

  spendingState: string;
  spendingAccount$ = this._spendingSvc.currentAccountInfo;
  accountCellConfig: HeaderLabelCellConfig;
  serviceState = this._spendingSvc.serviceState;

  constructor() {
    this._sub.add(this._serviceInventory.getService('spending')
      .pipe(
        filter(service => !!service),
        tap(service => {
          switch (service.state) {
            case ServiceState.COMPLETE:
              this.spendingState = ServiceState.COMPLETE;
              setTimeout(()=>this.setCompleteDate());
              break;
            case ServiceState.PENDING:
              this.spendingState = ServiceState.PENDING;
              setTimeout(()=>this.setPendingData());
              break;
            case ServiceState.ERROR:
              this.spendingState = ServiceState.ERROR;
              setTimeout(()=>this.setErrorData());
              break;
          }
        })
      ).subscribe()
    );

    this._sub.add(
      this.serviceState.pipe(
        tap(state => {
          switch (state) {
            case 'error':
              this.spendingState = ServiceState.ERROR;
              setTimeout(()=>this.setErrorData());
              break;
            case 'no-error':
              this.spendingState = ServiceState.COMPLETE;
              setTimeout(()=>this.setCompleteDate());
              break;
          }
        })
      ).subscribe()
    )
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  routeToSpending() {
    if (this.spendingState === ServiceState.COMPLETE) {
      this._router.navigate(['accounts', 'spending', 'dashboard']);
    } else if (this.spendingState === ServiceState.ERROR) {
      this.accountCellConfig = {
        leftTitle: this._translateSvc.instant('SPENDING'),
        lottieLoading: true        
      }
      setTimeout(()=>{this.retrySpending();}, 3000);
      this._cdRef.detectChanges();
    } else {
      return;
    }
  }

  private setErrorData() {
    this.accountCellConfig = {
      leftTitle: this._translateSvc.instant('ACCOUNT_ERROR_TITLE'),
      leftSubtitle: this._translateSvc.instant('ACCOUNT_ERROR_DESC')
    }
    this._cdRef.detectChanges();
  }

  private setPendingData() {
    this.accountCellConfig = {
      leftTitle: this._translateSvc.instant('SPENDING')
    };
    this._cdRef.detectChanges();
  }

  private setCompleteDate() {
    this.spendingAccount$.pipe(
      filter(account => !!account),
      tap( account=> {
        if (account) {
          this.accountCellConfig = {
            leftTitle: account.accountDisplayName,
            rightIconName: 'chevron_right',
            rightTitle: this._currencyPipe.transform(account.accountBalance)            
          }
          this._cdRef.detectChanges();
        }
      })
    ).subscribe();
  }

  private retrySpending() {
    setTimeout(() => {
      this._spendingSvc.getSpendingAccounts(true);
    }, 3000);
  }
}
